import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Layers, Sparkles, RefreshCw, Search, Plus, Trash2, Package, ChevronRight, X, Merge } from "lucide-react";
import { toast } from "sonner";
import UsageTip from "@/components/UsageTip";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

/**
 * Página dedicada de detección y organización de variantes en 2 niveles:
 *   Padre (modelo)  →  Sub-padre (dimensión / tamaño)  →  Variante (color)
 *
 * Ejemplo: Globos metalizados → 16 / 17 / 18 → Amarillo / Dorado / Palo rosa
 *
 * Extras vs. el modal anterior:
 * - Layout de 2 columnas: familias a la izquierda, editor de árbol a la derecha
 * - Se pueden AÑADIR colores manuales (Aleatorio, Surtido) que no vinieron en
 *   el Excel → se crean como productos placeholder (stock 0, es_placeholder=true).
 *   Al despachar el operador debe reasignar a un color con stock real.
 * - Se guarda una familia a la vez (más seguro, se puede pausar).
 */
export default function VariantesPage() {
  const [familias, setFamilias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [busqueda, setBusqueda] = useState("");
  const [saving, setSaving] = useState(false);
  const [familiaEdit, setFamiliaEdit] = useState(null);
  const [fusionOpen, setFusionOpen] = useState(false);
  const [fusionSearch, setFusionSearch] = useState("");
  const [fusionSel, setFusionSel] = useState(new Set()); // índices tildados en el modal
  // Indices de familias que fueron mergeadas dentro de la familia actual
  // (para eliminarlas del listado tras el "Guardar familia").
  const [fusionadasIdx, setFusionadasIdx] = useState([]);
  // Selección múltiple de variantes: keys son "sg-<sgi>-<vi>" o "dir-<vi>"
  const [varSel, setVarSel] = useState(new Set());
  const [moveDest, setMoveDest] = useState(""); // sub-grupo destino elegido

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/productos/_/detectar-jerarquia");
      setFamilias(data.grupos || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron detectar familias");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const familiasVisibles = useMemo(() => {
    if (!busqueda.trim()) return familias.map((f, i) => ({ f, i }));
    const q = busqueda.toLowerCase();
    return familias
      .map((f, i) => ({ f, i }))
      .filter(({ f }) =>
        f.sugerido_padre_nombre.toLowerCase().includes(q) ||
        f.base.toLowerCase().includes(q) ||
        (f.subgrupos || []).some((sg) =>
          sg.sugerido_nombre_sub_padre.toLowerCase().includes(q) ||
          (sg.variantes || []).some((v) => (v.nombre_original || "").toLowerCase().includes(q))
        ) ||
        (f.variantes_directas || []).some((v) => (v.nombre_original || "").toLowerCase().includes(q))
      );
  }, [familias, busqueda]);

  const selectFamilia = (idx, familia) => {
    setSelectedIdx(idx);
    setFamiliaEdit(JSON.parse(JSON.stringify(familia)));
    setFusionadasIdx([]);
  };

  const fusionarFamilias = (indices) => {
    if (!indices || indices.length === 0) return;
    setFamiliaEdit((f) => {
      const acc = { ...f, subgrupos: [...(f.subgrupos || [])], variantes_directas: [...(f.variantes_directas || [])] };
      indices.forEach((otraIdx) => {
        const otra = familias[otraIdx];
        if (!otra) return;
        // Traer subgrupos de la otra familia
        (otra.subgrupos || []).forEach((sg) => {
          acc.subgrupos.push({
            ...JSON.parse(JSON.stringify(sg)),
            padre_id_existente: null,
          });
        });
        // Traer variantes_directas al pool común
        if ((otra.variantes_directas || []).length > 0) {
          acc.variantes_directas = acc.variantes_directas.concat(
            JSON.parse(JSON.stringify(otra.variantes_directas))
          );
        }
      });
      // Auto-consolidar sub-grupos con el MISMO nombre (después de fusionar).
      // Ej: si dos familias tenían un sub-grupo "16", quedan como uno solo.
      const porNombre = new Map();
      acc.subgrupos.forEach((sg) => {
        const key = (sg.sugerido_nombre_sub_padre || "").trim().toLowerCase();
        if (porNombre.has(key)) {
          const existente = porNombre.get(key);
          existente.variantes = [...(existente.variantes || []), ...(sg.variantes || [])];
        } else {
          porNombre.set(key, { ...sg, variantes: [...(sg.variantes || [])] });
        }
      });
      acc.subgrupos = Array.from(porNombre.values());
      return acc;
    });
    setFusionadasIdx((arr) => [...arr, ...indices]);
    toast.success(
      indices.length === 1
        ? `Familia "${familias[indices[0]].sugerido_padre_nombre}" fusionada.`
        : `${indices.length} familias fusionadas. Sub-grupos con el mismo nombre se combinaron automáticamente.`
    );
    setFusionOpen(false);
    setFusionSel(new Set());
    setFusionSearch("");
  };

  const aplanarFamilia = () => {
    if (!familiaEdit) return;
    if (!confirm("¿Aplanar? Todos los productos de los sub-grupos irán a un pool común y podrás rearmar los sub-grupos desde cero sin duplicados.")) return;
    setFamiliaEdit((f) => {
      const pool = [...(f.variantes_directas || [])];
      (f.subgrupos || []).forEach((sg) => {
        (sg.variantes || []).forEach((v) => pool.push(v));
      });
      return { ...f, subgrupos: [], variantes_directas: pool };
    });
    setVarSel(new Set());
    toast.info("Todo aplanado al pool. Crea sub-grupos y mueve los productos con los checkboxes.");
  };

  const toggleFusionSel = (idx) => {
    setFusionSel((s) => {
      const next = new Set(s);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const totalVariantesEnFamilia = (f) => {
    if (!f) return 0;
    return (f.subgrupos || []).reduce((s, sg) => s + (sg.variantes || []).length, 0)
      + (f.variantes_directas || []).length;
  };

  const setPadreNombre = (v) =>
    setFamiliaEdit((f) => ({ ...f, sugerido_padre_nombre: v }));

  const setSubNombre = (sgi, v) =>
    setFamiliaEdit((f) => ({
      ...f,
      subgrupos: f.subgrupos.map((sg, i) => i === sgi ? { ...sg, sugerido_nombre_sub_padre: v } : sg),
    }));

  const setVarNombre = (sgi, vi, v) =>
    setFamiliaEdit((f) => ({
      ...f,
      subgrupos: f.subgrupos.map((sg, i) => i !== sgi ? sg : {
        ...sg,
        variantes: sg.variantes.map((x, j) => j === vi ? { ...x, nombre_limpio: v } : x),
      }),
    }));

  const setVarDirectaNombre = (vi, v) =>
    setFamiliaEdit((f) => ({
      ...f,
      variantes_directas: f.variantes_directas.map((x, j) => j === vi ? { ...x, nombre_limpio: v } : x),
    }));

  const removeSub = (sgi) => {
    if (!confirm("¿Quitar este sub-grupo de la familia? Los productos siguen intactos, solo salen de este agrupamiento.")) return;
    setFamiliaEdit((f) => ({ ...f, subgrupos: f.subgrupos.filter((_, i) => i !== sgi) }));
  };

  const removeVar = (sgi, vi) =>
    setFamiliaEdit((f) => ({
      ...f,
      subgrupos: f.subgrupos.map((sg, i) => i !== sgi ? sg : {
        ...sg, variantes: sg.variantes.filter((_, j) => j !== vi),
      }),
    }));

  const removeVarDirecta = (vi) =>
    setFamiliaEdit((f) => ({
      ...f,
      variantes_directas: f.variantes_directas.filter((_, j) => j !== vi),
    }));

  const addSub = () => {
    setFamiliaEdit((f) => ({
      ...f,
      subgrupos: [...(f.subgrupos || []), {
        spec: "nuevo",
        sugerido_nombre_sub_padre: `${f.sugerido_padre_nombre} Nuevo`,
        variantes: [],
      }],
    }));
  };

  const addColorManualEnSub = (sgi) => {
    const nombre = prompt("Nombre del color/variante manual (ej: Aleatorio, Surtido)", "Aleatorio");
    if (!nombre || !nombre.trim()) return;
    const clean = nombre.trim();
    setFamiliaEdit((f) => ({
      ...f,
      subgrupos: f.subgrupos.map((sg, i) => i !== sgi ? sg : {
        ...sg,
        variantes: [...(sg.variantes || []), {
          id: null,
          nombre_original: `[Manual] ${clean}`,
          nombre_limpio: clean,
          sku: null,
          color: clean.toLowerCase(),
          es_manual: true,
        }],
      }),
    }));
    toast.info(`Color "${clean}" agregado. Se creará como variante virtual (sin stock).`);
  };

  const addColorManualDirecto = () => {
    const nombre = prompt("Nombre del color/variante manual (ej: Aleatorio, Surtido)", "Aleatorio");
    if (!nombre || !nombre.trim()) return;
    const clean = nombre.trim();
    setFamiliaEdit((f) => ({
      ...f,
      variantes_directas: [...(f.variantes_directas || []), {
        id: null,
        nombre_original: `[Manual] ${clean}`,
        nombre_limpio: clean,
        sku: null,
        color: clean.toLowerCase(),
        es_manual: true,
      }],
    }));
    toast.info(`Color "${clean}" agregado.`);
  };

  const toggleVarSel = (key) => {
    setVarSel((s) => {
      const next = new Set(s);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const moverSeleccionadas = (destino) => {
    // destino: "dir" (pool directo), "new" (crear nuevo sub-grupo), o índice de sub-grupo
    if (varSel.size === 0) { toast.error("Marca al menos un producto."); return; }
    setFamiliaEdit((f) => {
      // Recolectar variantes seleccionadas y removerlas de origen
      const recogidas = [];
      const nuevosSubs = f.subgrupos.map((sg, sgi) => ({
        ...sg,
        variantes: (sg.variantes || []).filter((v, vi) => {
          if (varSel.has(`sg-${sgi}-${vi}`)) { recogidas.push(v); return false; }
          return true;
        }),
      }));
      const nuevasDir = (f.variantes_directas || []).filter((v, vi) => {
        if (varSel.has(`dir-${vi}`)) { recogidas.push(v); return false; }
        return true;
      });

      if (destino === "dir") {
        return { ...f, subgrupos: nuevosSubs, variantes_directas: [...nuevasDir, ...recogidas] };
      }
      if (destino === "new") {
        const nombre = prompt("Nombre del nuevo sub-grupo (ej: 16, 5cm)", "");
        if (!nombre || !nombre.trim()) return f;
        const clean = nombre.trim();
        return {
          ...f,
          subgrupos: [...nuevosSubs, {
            spec: clean,
            sugerido_nombre_sub_padre: `${f.sugerido_padre_nombre} ${clean}`,
            variantes: recogidas,
          }],
          variantes_directas: nuevasDir,
        };
      }
      // destino es índice numérico
      const idx = parseInt(destino, 10);
      if (Number.isNaN(idx) || !nuevosSubs[idx]) return f;
      nuevosSubs[idx] = {
        ...nuevosSubs[idx],
        variantes: [...(nuevosSubs[idx].variantes || []), ...recogidas],
      };
      return { ...f, subgrupos: nuevosSubs, variantes_directas: nuevasDir };
    });
    toast.success(`${varSel.size} producto(s) movido(s).`);
    setVarSel(new Set());
    setMoveDest("");
  };

  const guardarFamilia = async () => {
    if (!familiaEdit) return;
    const totalV = totalVariantesEnFamilia(familiaEdit);
    if (totalV === 0) { toast.error("Necesitas al menos una variante para guardar."); return; }
    if (!familiaEdit.sugerido_padre_nombre?.trim()) { toast.error("Ponle nombre al modelo padre."); return; }

    // Requerimos al menos una variante REAL (con id) para heredar valores
    const hasReal = (familiaEdit.subgrupos || []).some((sg) => (sg.variantes || []).some((v) => v.id))
      || (familiaEdit.variantes_directas || []).some((v) => v.id);
    if (!hasReal) {
      toast.error("Se necesita al menos una variante real (del contenedor) para heredar el precio/costo. Los colores manuales solos no bastan.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        nombre_padre: familiaEdit.sugerido_padre_nombre.trim(),
        subgrupos: (familiaEdit.subgrupos || [])
          .filter((sg) => (sg.variantes || []).length > 0)
          .map((sg) => ({
            nombre_sub_padre: sg.sugerido_nombre_sub_padre.trim(),
            padre_id_existente: sg.padre_id_existente || null,
            variantes: (sg.variantes || []).map((v) => ({
              id: v.id || null,
              nombre_limpio: (v.nombre_limpio || "").trim() || "Variante",
              color: v.color || null,
            })),
          })),
        variantes_directas: (familiaEdit.variantes_directas || []).map((v) => ({
          id: v.id || null,
          nombre_limpio: (v.nombre_limpio || "").trim() || "Variante",
          color: v.color || null,
        })),
      };
      const { data } = await api.post("/productos/_/agrupar-jerarquia", payload);
      toast.success(`Familia "${data.padre_nombre}" guardada: ${data.subpadres_creados} sub-grupos, ${data.variantes_actualizadas} variantes.`);
      // Quitar la familia guardada + todas las fusionadas del listado
      const idxAQuitar = new Set([selectedIdx, ...fusionadasIdx]);
      setFamilias((fs) => fs.filter((_, i) => !idxAQuitar.has(i)));
      setFamiliaEdit(null);
      setSelectedIdx(null);
      setFusionadasIdx([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar la familia");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <header className="mb-6">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Inventario</div>
            <h1 className="font-display text-4xl font-semibold tracking-tight text-foreground mt-2 flex items-center gap-3">
              <Layers className="w-8 h-8 text-primary" /> Detección de variantes
            </h1>
            <p className="text-muted-foreground mt-2 max-w-3xl">
              Organiza el catálogo en modelos → tamaños → colores. Ejemplo: <b>Globos metalizados</b> → <b>16, 17, 18</b> → <b>Dorado, Amarillo, Palo rosa</b>.
              También puedes añadir colores manuales (ej: <i>Aleatorio</i>, <i>Surtido</i>) que se crearán como variantes virtuales sin stock.
            </p>
          </div>
          <div className="flex items-end gap-2">
            <Button onClick={load} variant="outline" disabled={loading} className="h-11" data-testid="variantes-refresh">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Volver a analizar
            </Button>
          </div>
        </div>
      </header>

      <UsageTip storageKey="variantes-page-v4" steps={[
        "Flujo recomendado: <b>1)</b> Fusiona varias familias parecidas → <b>2)</b> pulsa <b>Aplanar</b> → todos los productos caen al pool → <b>3)</b> marca los que van juntos, dales <b>Nuevo sub-grupo</b> con el tamaño (16, 5cm) → <b>4)</b> repite con los demás.",
        "Los <b>checkboxes</b> en cada producto permiten mover varios de golpe: aparece una barra con los sub-grupos existentes.",
        "Al fusionar familias, los sub-grupos con <b>mismo nombre</b> se consolidan automáticamente (ya no duplicados).",
        "Los <b>colores manuales</b> (Aleatorio, Surtido) crean productos virtuales sin stock que el operador reasignará al despachar.",
      ]} />

      <div className="grid grid-cols-12 gap-4 mt-4">
        <aside className="col-span-12 lg:col-span-4 xl:col-span-3 bg-card border border-border rounded-xl overflow-hidden flex flex-col" style={{ maxHeight: "78vh" }}>
          <div className="p-3 border-b border-border">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <Input placeholder="Buscar familia o producto…" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} className="h-9 pl-8" data-testid="variantes-search" />
            </div>
            <div className="text-xs text-muted-foreground mt-2 flex items-center justify-between">
              <span><b className="text-foreground tabular-nums">{familiasVisibles.length}</b> familias</span>
              {loading && <span className="text-primary">Analizando…</span>}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-border/50" data-testid="variantes-familias-list">
            {familiasVisibles.length === 0 && !loading && (
              <div className="p-6 text-center text-sm text-muted-foreground">
                No hay familias por organizar. Todo tu catálogo ya está agrupado 🎉
              </div>
            )}
            {familiasVisibles.map(({ f, i }) => {
              const totalV = totalVariantesEnFamilia(f);
              const yaFusionada = fusionadasIdx.includes(i);
              const activa = selectedIdx === i;
              return (
                <button
                  key={i}
                  onClick={() => !yaFusionada && selectFamilia(i, f)}
                  disabled={yaFusionada}
                  className={`w-full text-left px-3 py-3 transition-colors ${
                    yaFusionada
                      ? "bg-violet-50 dark:bg-violet-950/30 opacity-70 cursor-not-allowed"
                      : `hover:bg-accent/40 ${activa ? "bg-accent" : ""}`
                  }`}
                  data-testid={`variantes-familia-${i}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold text-sm text-foreground truncate">{f.sugerido_padre_nombre}</div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        {yaFusionada ? (
                          <span className="text-violet-700 dark:text-violet-400 font-semibold">✨ Fusionada — se guardará junto</span>
                        ) : (
                          <>{(f.subgrupos || []).length} sub-grupo(s) · <span className="tabular-nums">{totalV}</span> variantes</>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        <main className="col-span-12 lg:col-span-8 xl:col-span-9">
          {!familiaEdit ? (
            <div className="bg-card border border-border rounded-xl p-12 text-center text-muted-foreground">
              <Package className="w-10 h-10 mx-auto text-muted-foreground/50 mb-3" />
              <p className="text-sm">Escoge una familia a la izquierda para empezar a organizarla.</p>
            </div>
          ) : (
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <div className="p-4 border-b border-border bg-gradient-to-r from-primary/5 to-transparent flex items-end justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[250px]">
                  <label className="text-[10px] uppercase tracking-wider font-bold text-primary">Modelo (nivel 1)</label>
                  <Input
                    value={familiaEdit.sugerido_padre_nombre}
                    onChange={(e) => setPadreNombre(e.target.value)}
                    className="h-12 text-lg font-display font-semibold mt-1"
                    placeholder="Ej: Globos metalizados"
                    data-testid="editor-padre-nombre"
                  />
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button variant="outline" size="sm" onClick={addSub} data-testid="editor-add-subgrupo">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Sub-grupo
                  </Button>
                  <Button variant="outline" size="sm" onClick={addColorManualDirecto} data-testid="editor-add-color-directo" title="Añadir color manual (sin sub-grupo)">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Color manual
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setFusionOpen(true)}
                    data-testid="editor-fusion"
                    title="Traer otras familias como sub-grupos de esta"
                    className="border-violet-400/60 text-violet-700 hover:bg-violet-50 dark:hover:bg-violet-950"
                  >
                    <Merge className="w-3.5 h-3.5 mr-1" /> Fusionar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={aplanarFamilia}
                    data-testid="editor-aplanar"
                    title="Vaciar todos los sub-grupos al pool para rearmar desde cero (útil tras fusionar)"
                    className="border-amber-400/60 text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-950"
                  >
                    <Layers className="w-3.5 h-3.5 mr-1" /> Aplanar
                  </Button>
                  <Button onClick={guardarFamilia} disabled={saving} className="bg-primary hover:bg-primary/90 h-10" data-testid="editor-save">
                    <Sparkles className="w-4 h-4 mr-2" /> {saving ? "Guardando..." : "Guardar familia"}
                  </Button>
                </div>
              </div>

              <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
                {/* Barra sticky de bulk-move cuando hay variantes seleccionadas */}
                {varSel.size > 0 && (
                  <div className="sticky top-0 z-10 bg-primary text-primary-foreground rounded-lg px-3 py-2 flex items-center gap-2 flex-wrap shadow-md" data-testid="bulk-bar">
                    <span className="text-sm font-semibold"><b className="tabular-nums">{varSel.size}</b> producto(s) marcado(s)</span>
                    <span className="text-xs opacity-80 mx-1">→ Mover a:</span>
                    <Button size="sm" variant="secondary" onClick={() => moverSeleccionadas("new")} data-testid="bulk-nuevo-sub" className="h-8">
                      <Plus className="w-3.5 h-3.5 mr-1" /> Nuevo sub-grupo
                    </Button>
                    {(familiaEdit.subgrupos || []).map((sg, i) => (
                      <Button
                        key={i}
                        size="sm"
                        variant="secondary"
                        onClick={() => moverSeleccionadas(String(i))}
                        className="h-8 max-w-[180px] truncate"
                        data-testid={`bulk-move-sg-${i}`}
                      >
                        {sg.sugerido_nombre_sub_padre || `Sub ${i + 1}`}
                      </Button>
                    ))}
                    <Button size="sm" variant="secondary" onClick={() => moverSeleccionadas("dir")} className="h-8" data-testid="bulk-move-pool">
                      Pool directo
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setVarSel(new Set())} className="h-8 ml-auto hover:bg-primary-foreground/10">
                      Limpiar
                    </Button>
                  </div>
                )}

                {(familiaEdit.subgrupos || []).map((sg, sgi) => (
                  <div key={sgi} className="border border-border rounded-lg p-3 bg-muted/20" data-testid={`subgrupo-${sgi}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="flex-1">
                        <label className="text-[10px] uppercase tracking-wider font-bold text-blue-700 dark:text-blue-400">Dimensión / Tamaño (nivel 2)</label>
                        <Input
                          value={sg.sugerido_nombre_sub_padre}
                          onChange={(e) => setSubNombre(sgi, e.target.value)}
                          className="h-10 font-semibold mt-1"
                          placeholder="Ej: Globos metalizados 16"
                          data-testid={`subgrupo-nombre-${sgi}`}
                        />
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => removeSub(sgi)} className="text-rose-600 hover:bg-rose-50 mt-6" data-testid={`subgrupo-remove-${sgi}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-2">
                      {(sg.variantes || []).map((v, vi) => {
                        const key = `sg-${sgi}-${vi}`;
                        return (
                          <VarianteCard
                            key={v.id || `manual-${sgi}-${vi}`}
                            variante={v}
                            onNombreChange={(val) => setVarNombre(sgi, vi, val)}
                            onRemove={() => removeVar(sgi, vi)}
                            checked={varSel.has(key)}
                            onToggle={() => toggleVarSel(key)}
                            testid={`variante-${sgi}-${vi}`}
                          />
                        );
                      })}
                      <button
                        onClick={() => addColorManualEnSub(sgi)}
                        className="border-2 border-dashed border-primary/40 rounded-lg p-3 hover:bg-primary/5 flex flex-col items-center justify-center gap-1 min-h-[86px] text-primary text-xs font-medium"
                        data-testid={`add-color-manual-${sgi}`}
                      >
                        <Plus className="w-4 h-4" />
                        Añadir color manual
                      </button>
                    </div>
                  </div>
                ))}

                {(familiaEdit.variantes_directas || []).length > 0 && (
                  <div className="border border-border rounded-lg p-3 bg-amber-50/40 dark:bg-amber-950/10" data-testid="directas-container">
                    <div className="flex items-center gap-2 mb-2 justify-between">
                      <div>
                        <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700 dark:text-amber-400">Pool sin agrupar</div>
                        <div className="text-[11px] text-muted-foreground italic">productos sueltos — márcalos y muévelos a un sub-grupo con la barra superior</div>
                      </div>
                      <div className="text-xs text-muted-foreground tabular-nums">{familiaEdit.variantes_directas.length} items</div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-2">
                      {familiaEdit.variantes_directas.map((v, vi) => {
                        const key = `dir-${vi}`;
                        return (
                          <VarianteCard
                            key={v.id || `manual-directo-${vi}`}
                            variante={v}
                            onNombreChange={(val) => setVarDirectaNombre(vi, val)}
                            onRemove={() => removeVarDirecta(vi)}
                            checked={varSel.has(key)}
                            onToggle={() => toggleVarSel(key)}
                            testid={`variante-directa-${vi}`}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Modal fusionar familia */}
      <Dialog open={fusionOpen} onOpenChange={(v) => {
        setFusionOpen(v);
        if (!v) { setFusionSearch(""); setFusionSel(new Set()); }
      }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="fusion-modal">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-violet-100 dark:bg-violet-950 text-violet-700 dark:text-violet-400 flex items-center justify-center">
                <Merge className="w-5 h-5" />
              </div>
              <div>
                <div className="text-[11px] font-bold uppercase text-violet-700 dark:text-violet-400 tracking-wider">Fusionar</div>
                <DialogTitle className="font-display text-2xl mt-1">Traer otras familias dentro de esta</DialogTitle>
              </div>
            </div>
            <DialogDescription className="mt-2">
              Marca las que quieras traer a <b>{familiaEdit?.sugerido_padre_nombre}</b> y toca el
              botón de abajo. Todas quedarán bajo un solo modelo padre cuando le des a
              "Guardar familia".
            </DialogDescription>
          </DialogHeader>

          <div className="relative mt-3">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <Input
              autoFocus
              placeholder="Buscar familia o producto…"
              value={fusionSearch}
              onChange={(e) => setFusionSearch(e.target.value)}
              className="h-10 pl-8"
              data-testid="fusion-search-input"
            />
            {fusionSearch && (
              <button
                onClick={() => setFusionSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground text-xs"
                data-testid="fusion-search-clear"
              >
                ✕
              </button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto divide-y divide-border/50 border border-border rounded-lg mt-2 min-h-[200px]">
            {(() => {
              const q = fusionSearch.trim().toLowerCase();
              const opciones = familias
                .map((f, i) => ({ f, i }))
                .filter(({ f, i }) => {
                  if (i === selectedIdx) return false;
                  if (fusionadasIdx.includes(i)) return false;
                  if (!q) return true;
                  if (f.sugerido_padre_nombre.toLowerCase().includes(q)) return true;
                  if (f.base.toLowerCase().includes(q)) return true;
                  const sgHit = (f.subgrupos || []).some((sg) =>
                    sg.sugerido_nombre_sub_padre.toLowerCase().includes(q) ||
                    (sg.variantes || []).some((v) => (v.nombre_original || "").toLowerCase().includes(q))
                  );
                  if (sgHit) return true;
                  return (f.variantes_directas || []).some((v) =>
                    (v.nombre_original || "").toLowerCase().includes(q)
                  );
                });
              if (opciones.length === 0) {
                return (
                  <div className="p-6 text-center text-sm text-muted-foreground">
                    {q ? `No hay familias que coincidan con "${fusionSearch}".` : "No hay más familias disponibles para fusionar."}
                  </div>
                );
              }
              return opciones.map(({ f, i }) => {
                const total = totalVariantesEnFamilia(f);
                const checked = fusionSel.has(i);
                return (
                  <label
                    key={i}
                    className={`w-full text-left px-4 py-3 flex items-center gap-3 cursor-pointer transition-colors ${checked ? "bg-violet-50 dark:bg-violet-950/30" : "hover:bg-accent/40"}`}
                    data-testid={`fusion-opcion-${i}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleFusionSel(i)}
                      className="w-4 h-4 accent-violet-600"
                      data-testid={`fusion-check-${i}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-foreground truncate">{f.sugerido_padre_nombre}</div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        {(f.subgrupos || []).length} sub-grupo(s) · <span className="tabular-nums">{total}</span> variantes
                      </div>
                    </div>
                  </label>
                );
              });
            })()}
          </div>

          <div className="flex items-center justify-between gap-2 mt-3">
            <div className="text-xs text-muted-foreground">
              {fusionSel.size > 0
                ? <><b className="text-foreground tabular-nums">{fusionSel.size}</b> familia(s) seleccionada(s)</>
                : "Marca una o varias familias con el checkbox."}
            </div>
            <div className="flex gap-2">
              {fusionSel.size > 0 && (
                <Button variant="ghost" size="sm" onClick={() => setFusionSel(new Set())} data-testid="fusion-clear-sel">
                  Limpiar
                </Button>
              )}
              <Button variant="outline" onClick={() => setFusionOpen(false)}>Cancelar</Button>
              <Button
                onClick={() => fusionarFamilias(Array.from(fusionSel))}
                disabled={fusionSel.size === 0}
                className="bg-violet-600 hover:bg-violet-700 text-white"
                data-testid="fusion-confirm"
              >
                <Merge className="w-4 h-4 mr-2" />
                Traer {fusionSel.size > 0 ? fusionSel.size : ""} familia{fusionSel.size !== 1 ? "s" : ""}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function VarianteCard({ variante, onNombreChange, onRemove, checked, onToggle, testid }) {
  const esManual = !variante.id;
  return (
    <div
      className={`rounded-lg p-2 flex flex-col gap-1 relative border transition-all ${
        checked
          ? "border-primary bg-primary/10 ring-2 ring-primary/40"
          : esManual
            ? "border-emerald-500/40 bg-emerald-50/40 dark:bg-emerald-950/10"
            : "border-border bg-card"
      }`}
      data-testid={testid}
    >
      {onToggle && (
        <input
          type="checkbox"
          checked={!!checked}
          onChange={onToggle}
          className="absolute top-1.5 left-1.5 w-4 h-4 accent-primary z-10 cursor-pointer"
          title="Marcar para mover en bloque"
          data-testid={`${testid}-check`}
        />
      )}
      <button
        onClick={onRemove}
        className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-rose-500 hover:bg-rose-600 text-white flex items-center justify-center shadow-sm z-10"
        title="Quitar de este grupo"
      >
        <X className="w-3 h-3" />
      </button>
      <Input
        value={variante.nombre_limpio || ""}
        onChange={(e) => onNombreChange(e.target.value)}
        className={`h-8 text-sm font-medium ${onToggle ? "pl-7" : ""}`}
        placeholder="Color / etiqueta"
      />
      <div className="text-[10px] text-muted-foreground leading-tight truncate" title={variante.nombre_original}>
        {esManual ? (
          <span className="text-emerald-700 dark:text-emerald-400 font-semibold">✨ Manual (sin stock)</span>
        ) : (
          <span>← {variante.nombre_original}</span>
        )}
      </div>
      {variante.sku && (
        <div className="text-[9px] font-mono text-muted-foreground/70 truncate">{variante.sku}</div>
      )}
    </div>
  );
}
